import {Grid, Section} from '@primer/react-brand/esm'
import type {ReactNode} from 'react'
import {FlexSuiteIntroIconCards, FlexSuiteIntroImageCards} from '../../../components/FlexSuiteIntroCards'
import {FlexSuiteIntroLogos} from '../../../components/FlexSuiteIntroLogos'
import {FlexSuiteIntroPillars} from '../../../components/FlexSuiteIntroPillars'
import {FlexSuiteIntroSimple} from '../../../components/FlexSuiteIntroSimple'
import {FlexSuiteSectionIntro} from '../../../components/FlexSuiteSectionIntro'
import {FlexSuiteStatistics} from '../../../components/FlexSuiteStatistics'
import {hasIntroSemanticHeading} from '../../../utils'
import type {IntroSectionProps} from './types'

export function IntroSection({
  introLogoHeading,
  introLogoType,
  introLogos,
  introLogoLink,
  introContext,
  introEyebrow,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading = true,
  hasMarqueeTopBorder = false,
}: IntroSectionProps) {
  const promoteFirstHeading = !hasIntroSemanticHeading({
    hasLogoHeading: introLogoType === 'Marquee' && Boolean(introLogos?.length),
    hasIntroContextHeading: Boolean(introContextHeading),
  })
  const introContextContent = renderIntroContextContent({
    introContext,
    introEyebrow,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
    promoteFirstHeading,
  })

  const hasIntroContent = Boolean(introLogos?.length) || introContextContent !== null

  if (!hasIntroContent) return null

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      <FlexSuiteIntroLogos
        heading={introLogoHeading}
        logos={introLogos}
        layout={introLogoType === 'Marquee' ? 'logobar' : 'tiles'}
        logobarOptions={{...logobarOptions, marquee: true, takeoverButton}}
        logoSuiteOptions={logoSuiteOptions}
        hideHeading={hideLogoHeading}
        hasTopBorder={hasMarqueeTopBorder}
      />
      {introContextContent}
    </Section>
  )
}

type IntroContextContentProps = Pick<
  IntroSectionProps,
  | 'introContext'
  | 'introEyebrow'
  | 'introContextHeading'
  | 'introContextLink'
  | 'introContextBody'
  | 'introItems'
  | 'introIconCards'
  | 'introPillars'
  | 'introImageCards'
  | 'introStats'
> & {promoteFirstHeading: boolean}

function renderIntroContextContent({
  introContext,
  introEyebrow,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  promoteFirstHeading,
}: IntroContextContentProps): ReactNode {
  if (!introContext) return null

  const sharedIntroProps = {
    introEyebrow,
    introContextHeading,
    introContextBody,
    introContextLink,
  }

  switch (introContext) {
    case 'Simple Intro':
      return (
        <Grid enableGutters={false}>
          <Grid.Column>
            <FlexSuiteIntroSimple
              heading={introContextHeading}
              description={introContextBody}
              link={introContextLink}
              items={introItems}
              promoteFirstHeading={promoteFirstHeading}
              variant="gridline"
              headingSize="5"
              iconSize="medium"
            />
          </Grid.Column>
        </Grid>
      )
    case 'Pillars':
      return (
        <>
          <SectionIntroContext {...sharedIntroProps} />
          <FlexSuiteIntroPillars introPillars={introPillars} promoteFirstHeading={promoteFirstHeading} />
        </>
      )
    case 'Icon Cards':
      return (
        <>
          <SectionIntroContext {...sharedIntroProps} />
          <FlexSuiteIntroIconCards cards={introIconCards} promoteFirstHeading={promoteFirstHeading} />
        </>
      )
    case 'Image Cards':
      return (
        <>
          <SectionIntroContext {...sharedIntroProps} />
          <FlexSuiteIntroImageCards cards={introImageCards} promoteFirstHeading={promoteFirstHeading} />
        </>
      )
    case 'Stats':
      return (
        <>
          <SectionIntroContext {...sharedIntroProps} />
          <FlexSuiteStatistics stats={introStats} />
        </>
      )
    default:
      return null
  }
}

type IntroContextSectionIntroProps = Pick<
  IntroSectionProps,
  'introEyebrow' | 'introContextHeading' | 'introContextBody' | 'introContextLink'
>

function SectionIntroContext({
  introEyebrow,
  introContextHeading,
  introContextBody,
  introContextLink,
}: IntroContextSectionIntroProps) {
  if (!introContextHeading) return null

  const introContextLinkText = introContextLink?.fields.text?.trim()

  return (
    <FlexSuiteSectionIntro
      label={introEyebrow}
      heading={introContextHeading}
      description={introContextBody}
      link={introContextLinkText ? introContextLink : undefined}
      hasBottomBorder
    />
  )
}
